import { useStyles } from "./Profile.style";
import { ProfileInfo } from "../../components/common/ProfileInfo";
import { useEffect, useMemo, useState } from "react";
import { CoinType } from "@onmo/graphql/graphql";
import {
  getConfigGQL,
  getUserProfileTgpGQL,
  meGQL,
  getStrangeProfileTgpGQL,
  getUserVodabucksTransactionsGQL,
} from "../../graphql";
import { useQuery } from "@apollo/client";
import { Query } from "@onmo/graphql/graphql";
import { ProfileAchievement } from "../../components/common/ProfileAchievement";
import { User } from "../../models/user";
import { useParams, useSearchParams } from "react-router-dom";
import { USER_PROFILE_TABS } from "../../constants/userProfile";
import ProfileActivity from "../../components/common/ProfileActivity/ProfileActivity";
import { ProfileStats } from "../../components/common/ProfileStats";
import { VodabucksStats } from "../../components/common/VodabucksStats";
import { PRODUCT_QUERY } from "../../constants/achievement";
import { useVodabucksBalance } from "../../hooks/useVodabucksBalance";
import { useWalletConfig } from "../../hooks/useWalletConfig";
import { getWalletBalance } from "../../constants/wallet";
import { useAppSelector } from "../../components/hooks";

const PROFILE_TAB_MAP: Record<string, string> = {
  [USER_PROFILE_TABS.stats]: "tab_Stats",
  [USER_PROFILE_TABS.achievements]: "tab_Achievements",
  [USER_PROFILE_TABS.vodaBucks]: "tab_VodaBucks",
  [USER_PROFILE_TABS.recentActivity]: "tab_Recent Activities",
};

const Profile: React.FC = () => {
  const classes = useStyles();
  const { isCurrencyEnabled, walletConfig } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;
  const showVodabucks = isCurrencyEnabled(CoinType.Vodabucks);
  const profileItems = useAppSelector(
    (state) => state.theme.theme?.pages?.profile || [],
  );

  const visibleProfileTabs = useMemo(() => {
    return profileItems
      .filter((item) => {
        if (item === USER_PROFILE_TABS.vodaBucks && !showVodabucks)
          return false;
        return true;
      })
      .map((item) => {
        return PROFILE_TAB_MAP[item];
      })
      .filter(Boolean);
  }, [profileItems, showVodabucks]);

  const [activeTab, setActiveTab] = useState(visibleProfileTabs[0]);
  const { userId: userIdParam }: { userId?: string } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");

  const { data: dataMe } = useQuery<Query>(meGQL);
  const { data: dataConfig } = useQuery(getConfigGQL);
  const { data: dataProfile } = useQuery<{ user: Query["user"] }>(
    userIdParam ? getStrangeProfileTgpGQL : getUserProfileTgpGQL,
    {
      variables: {
        id: userIdParam || dataMe?.me.id,
        achievementsInput: { products: PRODUCT_QUERY },
        transactionsInput: { coinTypes: baseCoinType },
      },
      fetchPolicy: "cache-and-network",
    },
  );
  const { data: dataVodabucks, loading: loadingVodabucks } = useQuery<{
    user: Query["user"];
  }>(getUserVodabucksTransactionsGQL, {
    variables: {
      id: dataMe?.me.id,
      transactionsInput: { coinTypes: [CoinType.Vodabucks] },
    },
    skip: !showVodabucks || !!userIdParam || !dataMe?.me.id,
    fetchPolicy: "cache-and-network",
  });

  const profileUserId = userIdParam || dataMe?.me?.id;
  const { vbBalance } = useVodabucksBalance(profileUserId);

  const userRank = User.getCurrentRank({
    wallet: dataProfile?.user?.coinWallets || dataMe?.me?.coinWallets,
    tierLevels: dataConfig?.config.tierLevels,
    baseCoinType,
    xp: dataProfile?.user?.xp || dataMe?.me?.xp,
  });
  const gzTokens = getWalletBalance(
    dataMe?.me?.coinWallets || [],
    baseCoinType,
  );

  useEffect(() => {
    if (tab && visibleProfileTabs.includes(PROFILE_TAB_MAP[tab])) {
      setActiveTab(PROFILE_TAB_MAP[tab] ?? visibleProfileTabs[0]);
    }
  }, [tab]);

  return (
    <div className={classes.root}>
      {userRank && (
        <ProfileInfo
          listTabs={visibleProfileTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          profile={dataProfile?.user}
          userRank={userRank}
          gzTokens={gzTokens}
        />
      )}
      {activeTab.includes("Stats") && !userIdParam && dataMe?.me && (
        <ProfileStats
          profile={dataProfile?.user}
          wallet={dataMe?.me.coinWallets}
          rewardsHistory={dataProfile?.user?.transactions?.items}
        />
      )}
      {activeTab.includes("Recent") && !userIdParam && dataMe?.me && (
        <ProfileActivity me={dataMe?.me} />
      )}
      {(activeTab.includes("Achievements") || !!userIdParam) && (
        <ProfileAchievement
          achievements={dataProfile?.user?.achievements?.items}
        />
      )}
      {activeTab.includes("VodaBucks") && showVodabucks && (
        <VodabucksStats
          balance={vbBalance}
          vodabucksHistory={dataVodabucks?.user?.transactions?.items}
          loading={loadingVodabucks && !dataVodabucks}
        />
      )}
    </div>
  );
};

export default Profile;
